/**
 * Tile detail drawer (P6.4).
 *
 * Right-side dismissable panel; surfaces a tile's mapped services
 * with per-service state (confidence, resilience pillar, open-finding
 * count, MVB linkage).
 *
 * Mounts when ``tileId`` is non-null; useTileDetail fires lazily.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useTileDetail } from "twin/api/hooks";
import type {
  LandscapeConfidence,
  LandscapePillar,
  TileMappedService,
} from "shared/api/types";

const CONFIDENCE_PILL: Record<LandscapeConfidence, string> = {
  high: "bg-emerald-100 text-emerald-900",
  medium: "bg-sky-100 text-sky-900",
  low: "bg-amber-100 text-amber-900",
  unverified: "bg-slate-100 text-slate-700",
};

const PILLAR_PILL: Record<LandscapePillar, string> = {
  red: "bg-rose-200 text-rose-900",
  amber: "bg-amber-200 text-amber-900",
  green: "bg-emerald-200 text-emerald-900",
  unknown: "bg-slate-100 text-slate-600",
};

export interface TileDetailDrawerProps {
  tileId: string | null;
  as_of?: string | undefined;
  onClose: () => void;
}

export function TileDetailDrawer({ tileId, as_of, onClose }: TileDetailDrawerProps) {
  const { t } = useTranslation();
  const query = useTileDetail(tileId, { as_of });

  if (tileId === null) {
    return null;
  }

  return (
    <aside
      data-testid="tile-detail-drawer"
      role="dialog"
      aria-label="Tile detail"
      className="fixed inset-y-0 end-0 z-30 w-full max-w-md overflow-y-auto border-s border-slate-200 bg-white shadow-xl"
    >
      <header className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white p-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {query.data?.name ?? t("screens.tileDetailDrawer.tile")}
          </h2>
          {query.data?.pascal_name && (
            <code className="text-xs text-slate-500">{query.data.pascal_name}</code>
          )}
        </div>
        <button
          type="button"
          data-testid="tile-drawer-close"
          onClick={onClose}
          className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          aria-label="Close drawer"
        >
          ×
        </button>
      </header>
      <div className="p-4">
        {query.isLoading && (
          <div className="text-sm text-slate-500">
            {t("screens.tileDetailDrawer.loading")}
          </div>
        )}
        {query.error && (
          <div className="rounded bg-rose-50 p-3 text-sm text-rose-900">
            {t("screens.tileDetailDrawer.failedToLoadTileDetail")} {query.error.message}
          </div>
        )}
        {query.data && query.data.mapped_services.length === 0 && (
          <div
            data-testid="tile-drawer-empty"
            className="rounded bg-slate-50 p-3 text-sm text-slate-600"
          >
            {t("screens.tileDetailDrawer.noServicesMappedToThisTileYet")}
          </div>
        )}
        {query.data && query.data.mapped_services.length > 0 && (
          <ul className="space-y-3">
            {query.data.mapped_services.map((svc) => (
              <MappedServiceRow key={svc.service_id} svc={svc} />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function MappedServiceRow({ svc }: { svc: TileMappedService }) {
  const { t } = useTranslation();
  return (
    <li
      data-testid="tile-drawer-service"
      data-service-id={svc.service_id}
      className="rounded border border-slate-200 p-3 hover:border-slate-300"
    >
      <div className="flex items-baseline justify-between gap-2">
        <Link
          to={`/twin/service/${svc.service_id}`}
          className="font-medium text-slate-900 hover:underline"
        >
          {svc.service_name}
        </Link>
        <span className="text-xs text-slate-500 tabular-nums">
          {svc.classification}
        </span>
      </div>
      <code className="text-xs text-slate-500">{svc.service_code}</code>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
        <span
          data-testid="confidence-chip"
          className={`rounded px-1.5 py-0.5 ${CONFIDENCE_PILL[svc.confidence]}`}
        >
          {t("screens.tileDetailDrawer.conf")} {svc.confidence}
        </span>
        {svc.resilience_pillar && (
          <span
            data-testid="pillar-chip"
            className={`rounded px-1.5 py-0.5 ${PILLAR_PILL[svc.resilience_pillar]}`}
          >
            {t("screens.tileDetailDrawer.pillar")} {svc.resilience_pillar}
          </span>
        )}
        {svc.is_mvb_linked && (
          <span
            data-testid="mvb-chip"
            className="rounded bg-violet-200 px-1.5 py-0.5 text-violet-900"
          >
            {t("screens.tileDetailDrawer.mvb")}
          </span>
        )}
        {svc.open_finding_count > 0 && (
          <span className="rounded bg-rose-100 px-1.5 py-0.5 text-rose-900">
            {svc.open_finding_count} {t("screens.tileDetailDrawer.open")}
          </span>
        )}
      </div>
      {svc.notes && <p className="mt-2 text-xs text-slate-600">{svc.notes}</p>}
    </li>
  );
}
